import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * Who may do what in this edition: its roles, and who holds each.
 *
 * **The server decides everything that matters here.** It refuses any grant the caller does not
 * hold themselves, any change that leaves the edition without an owner, and any change that would
 * lock the caller out of this very screen — each with a `detail.code` the toast turns into a
 * sentence. What this file adds is the shape of the editor: which grants exist, grouped by module
 * and tier, so the grid can say "manage includes write includes read" before anybody saves.
 */

export const roleSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
  grants: z.array(z.string()),
  memberCount: z.number(),
})

export type Role = z.infer<typeof roleSchema>

const roleListSchema = z.object({ items: z.array(roleSchema), grantable: z.array(z.string()) })

export type RoleList = z.infer<typeof roleListSchema>

export const memberSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  email: z.string(),
  name: z.string(),
  role: z.string(),
  joinedAt: z.string(),
  isSelf: z.boolean(),
  banned: z.boolean(),
})

export type Member = z.infer<typeof memberSchema>

const memberListSchema = z.object({ items: z.array(memberSchema) })

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

export function fetchRoles(signal?: AbortSignal): Promise<RoleList> {
  return api.get('/v1/identity/admin/roles', { schema: roleListSchema, ...withSignal(signal) })
}

export function fetchMembers(signal?: AbortSignal): Promise<{ items: Member[] }> {
  return api.get('/v1/identity/admin/members', { schema: memberListSchema, ...withSignal(signal) })
}

export type RoleInput = { key: string; name: string; grants: string[] }

export function createRole(input: RoleInput): Promise<Role> {
  return api.post('/v1/identity/admin/roles', { schema: roleSchema, body: input })
}

export function updateRole(
  roleId: string,
  input: { name?: string; grants?: string[] },
): Promise<Role> {
  return api.patch(`/v1/identity/admin/roles/${roleId}`, { schema: roleSchema, body: input })
}

export function deleteRole(roleId: string, reassignTo?: string): Promise<{ reassigned: number }> {
  const query = reassignTo === undefined ? '' : `?reassignTo=${encodeURIComponent(reassignTo)}`
  return api.delete(`/v1/identity/admin/roles/${roleId}${query}`, {
    schema: z.object({ reassigned: z.number() }),
  })
}

export type AddMemberInput = {
  email: string
  name?: string
  role: string
  sendSignInLink: boolean
}

export function addMember(input: AddMemberInput): Promise<Member & { signInLinkSent: boolean }> {
  return api.post('/v1/identity/admin/members', {
    schema: memberSchema.extend({ signInLinkSent: z.boolean() }),
    body: input,
  })
}

export function changeMemberRole(memberId: string, role: string): Promise<Member> {
  return api.put(`/v1/identity/admin/members/${memberId}/role`, {
    schema: memberSchema,
    body: { role },
  })
}

export function removeMember(memberId: string): Promise<{ removed: true }> {
  return api.delete(`/v1/identity/admin/members/${memberId}`, {
    schema: z.object({ removed: z.literal(true) }),
  })
}

// --- The grant grid ----------------------------------------------------------------------------

/**
 * The four tiers, in the order the grid shows them. Two are the **portal** (what a delegate or an
 * exhibitor may do with their own things) and two are the **console** (what an operator may do with
 * everybody's). `view` is the console read-only tier and deliberately not `read` — see the API's
 * `rbac.ts`.
 */
export const TIERS = ['read', 'write', 'view', 'manage'] as const
export type Tier = (typeof TIERS)[number]

export const TIER_LABELS: Record<Tier, { group: string; label: string; help: string }> = {
  read: { group: 'Portal', label: 'Read', help: 'Sees their own records in the portal.' },
  write: { group: 'Portal', label: 'Write', help: 'Registers, applies or votes in the portal.' },
  view: { group: 'Console', label: 'View', help: 'Opens the console pages, read-only.' },
  manage: {
    group: 'Console',
    label: 'Manage',
    help: 'Opens the console pages and changes things.',
  },
}

/**
 * The tiers a row can have — every tier for a module, and only `manage` for `access` (members and
 * roles), which has no read-only or portal side. Read from what the server lists as grantable, so
 * the grid never offers a grant the server would refuse.
 */
export function tiersFor(grantable: readonly string[], module: string): Tier[] {
  return TIERS.filter((tier) => grantable.includes(`${module}.${tier}`))
}

/** Full control of the edition. Only an owner can hand it out, and the server says so. */
export const SUPERADMIN_GRANT = '*.*'

/**
 * The modules the grid has rows for, in the order the server listed their grants.
 *
 * Derived from `grantable` rather than from the manifest, so a module this tenant has not bought
 * still appears: a grant is a permission, not an entitlement, and a role written today should not
 * have to be rewritten the day the module is switched on.
 */
export function grantModules(grantable: readonly string[]): string[] {
  const modules: string[] = []
  for (const grant of grantable) {
    const [module] = grant.split('.')
    if (grant !== SUPERADMIN_GRANT && module !== undefined && !modules.includes(module)) {
      modules.push(module)
    }
  }
  return modules
}

/**
 * The tier a set of grants gives a module: the highest held, or null.
 *
 * One tier per module rather than three checkboxes, because `.manage` implies `.write` implies
 * `.read` — a role holding manage and not read is a state the server would read as manage anyway,
 * and a grid that let you draw it would be lying about what it grants.
 */
export function tierOf(grants: readonly string[], module: string): Tier | null {
  // Highest first: manage implies view, write and read; view implies read.
  for (const tier of ['manage', 'view', 'write', 'read'] as const) {
    if (grants.includes(`${module}.${tier}`)) return tier
  }
  return null
}

/** Set one module's tier, replacing whatever it had. `null` removes the module. */
export function withTier(grants: readonly string[], module: string, tier: Tier | null): string[] {
  const others = grants.filter((grant) => !grant.startsWith(`${module}.`))
  return (tier === null ? others : [...others, `${module}.${tier}`]).toSorted()
}

/** A role key from a display name: `Registration desk` → `registration-desk`. */
export function keyFromName(name: string): string {
  return (
    name
      .normalize('NFKD')
      // The accents NFKD split off, dropped rather than hyphenated: `Équipe` → `equipe`.
      .replaceAll(/\p{M}/gu, '')
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '-')
      .replaceAll(/^-+|-+$/g, '')
      .slice(0, 40)
  )
}

/** What a module is called on screen. The keys are already words; this only spaces them. */
/**
 * The names the sidebar uses, not the module keys: an operator granting "Rundown" is guessing that
 * it means the Program page. An unknown key falls through to itself, spaced.
 */
const MODULE_LABELS: Record<string, string> = {
  access: 'Members and roles',
  identity: 'Theme and companies',
  dashboard: 'Dashboard',
  billing: 'Billing',
  ticket: 'Registrations',
  onsite: 'Onsite check-in',
  exhibitor: 'Exhibitors and floor',
  voting: 'Voting',
  room: 'Rooms',
  rundown: 'Program',
  'business-matching': 'Meetings',
  cms: 'Content',
  integration: 'Integrations',
}

export function moduleLabel(module: string): string {
  const known = MODULE_LABELS[module]
  if (known !== undefined) return known
  const spaced = module.replaceAll('-', ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
